import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Collapse, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { InfiniteScroll } from '../InfiniteScroll';
import classNames from 'classnames';
import { IMeta } from '../../../../REST';
import { ITariffsList } from '../../../../interfaces';
import { useInputDebounce } from '../../../../hooks';
import { IParams, makeSelectionLabel } from '../../../../instruments';
import { tB, tCustom } from '../../../../translate';
import TariffsStyles from '../../m_styles.less';

const { Panel } = Collapse;

interface ISelectableItem {
    id: string;
    name: string;
    countryName?: string;
}

interface ISelectableListProps {
    title: string;
    selectedItems: string[];
    setSelected: React.Dispatch<React.SetStateAction<string[]>>;
    searchPlaceholder?: string;
    emptyMessage?: string;
    fetchFunction: any;
    initPage?: number;
    initItems?: any;
    initTotal?: number;
    pagination?: boolean;
    isNetworks?: boolean;
    filter?: IParams;
}

export const SelectableList: React.FC<ISelectableListProps> = ({
    title,
    selectedItems,
    setSelected,
    fetchFunction,
    searchPlaceholder = tCustom('Search item...', { item: title.toLowerCase() }),
    emptyMessage = tCustom('No item found', { item: title.toLowerCase() }),
    pagination = true,
    filter = {} as IParams,
    isNetworks,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useInputDebounce(searchTerm);
    const [page, setPage] = useState(1);
    const [items, setItems] = useState<ISelectableItem[]>([]);
    const [total, setTotal] = useState(0);

    const pageSize = 100;

    const fetchData = async (pageNumber: number) => {
        const resp: { list: ITariffsList; meta: IMeta } = (await fetchFunction({
            pageNumber: pageNumber,
            pageSize: pageSize,
            search: debouncedSearchTerm,
            ...filter,
        })) as { list: ITariffsList; meta: IMeta };

        const { list = [], meta } = resp;
        const { records_count } = meta;

        if (pagination) {
            setItems((prevList) => (pageNumber === 1 ? list : [...prevList, ...list]));
            setTotal(records_count || 0);
        } else {
            setItems(list);
        }
    };

    const handleToggle = (operatorId: string) => {
        setSelected((prev: string[]) => {
            if (prev.includes(operatorId)) {
                return prev.filter((id: string) => id !== operatorId);
            } else {
                return [...prev, operatorId];
            }
        });
    };

    const reset = () => {
        setSelected([]);
    };

    const filteredItems = useMemo(() => {
        if (!searchTerm.trim()) {
            return items;
        }

        const lowerSearchTerm = searchTerm.toLowerCase();
        return items.filter(
            (item) =>
                item.name.toLowerCase().includes(lowerSearchTerm) || item.id.toLowerCase().includes(lowerSearchTerm),
        );
    }, [items, searchTerm]);

    const allFilteredSelected =
        filteredItems?.length > 0 && filteredItems.every((item) => selectedItems.includes(item.id));

    const handleSelectAllFiltered = () => {
        if (allFilteredSelected) {
            filteredItems.forEach((item) => {
                if (selectedItems.includes(item.id)) {
                    handleToggle(item.id);
                }
            });
        } else {
            filteredItems.forEach((item) => {
                if (!selectedItems.includes(item.id)) {
                    handleToggle(item.id);
                }
            });
        }
    };

    useEffect(() => {
        setPage(1);
        setItems([]);
        fetchData(1);
    }, [debouncedSearchTerm, JSON.stringify(filter)]);

    return (
        <Collapse>
            <Panel key={1} header={title} forceRender={true}>
                <div className={TariffsStyles.selectableListContainer}>
                    <div className={TariffsStyles.header}>
                        <h4 className={TariffsStyles.title}>{title}</h4>
                        <div className={TariffsStyles.count}>{makeSelectionLabel(title, selectedItems.length)}</div>
                    </div>

                    <div className={TariffsStyles.inputContainer}>
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            allowClear
                            className={TariffsStyles.input}
                        />
                    </div>

                    <div className={TariffsStyles.searchContainer}>
                        <Button onClick={reset} size="small" disabled={!items.length}>
                            {tB('Reset')}
                        </Button>

                        {searchTerm && filteredItems.length > 0 && (
                            <Button onClick={handleSelectAllFiltered} size="small" type="dashed">
                                {tB(allFilteredSelected ? 'Deselect Filtered' : 'Select Filtered')}
                            </Button>
                        )}
                    </div>

                    <div className={TariffsStyles.scrollContainer}>
                        <div className={TariffsStyles.listContainer}>
                            {filteredItems.length === 0 ? (
                                <div className={TariffsStyles.notFound}>
                                    {searchTerm ? tCustom('No item found', { item: searchTerm }) : emptyMessage}
                                </div>
                            ) : (
                                <InfiniteScroll
                                    pageSize={pageSize}
                                    page={page}
                                    fetchFunc={fetchData}
                                    totalPackage={total}
                                    currentPackages={items.length}
                                    handleSetPage={setPage}
                                >
                                    {filteredItems.map((item) => (
                                        <div
                                            key={item.id}
                                            className={classNames(TariffsStyles.listItem, {
                                                [TariffsStyles.listItemSelected]: selectedItems.includes(item.id),
                                            })}
                                            onClick={() => handleToggle(item.id)}
                                        >
                                            <div className={TariffsStyles.info}>
                                                <span className={TariffsStyles.name}>{item.name}</span>
                                                <span className={TariffsStyles.id}>
                                                    {isNetworks ? item.countryName : `ID: ${item.id}`}
                                                </span>
                                            </div>
                                            <Checkbox checked={selectedItems.includes(item.id)} />
                                        </div>
                                    ))}
                                </InfiniteScroll>
                            )}
                        </div>
                    </div>

                    {searchTerm && (
                        <div className={TariffsStyles.total}>
                            {tCustom('Showing found of total title', {
                                found: filteredItems.length,
                                total: items.length,
                                title: title.toLowerCase(),
                            })}
                        </div>
                    )}
                </div>
            </Panel>
        </Collapse>
    );
};
